import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import submit from "submit-form";
import {
  DIETARY_TYPES,
  FOOD_TYPES,
  MACROS_BASE_TYPES,
  S3,
} from "../../../../../../constants";
import Dropdown from "../../../../../../parts/dropdown";
import ImageWidgetUploadInput from "../../../../../../parts/inputs/image-widget-upload";
import NutrientInput from "../../../../../../parts/inputs/nutrient-input";
import { RadioMultipleVerticalGroup } from "../../../../../../parts/inputs/radio-multiple-vertical-group";
import RadioVerticalGroup from "../../../../../../parts/inputs/radio-vertical-group";
import NutrientsChartForWidget from "../../../../../athlete-portal/nutrition/components/NutrientsChartForWidget";
import scriptLoader from "../../../../../../utils/scriptLoader";

const DIETARY_I18N = {
  Vegetarian: "vegetarian",
  "Gluten-free": "glutenFree",
  Vegan: "vegan",
};
import SvgIcon from "../../../../../../parts/SvgIcon";
import ToolTip from "../../../../../../parts/tooltip";

export const EditRecipe = ({
  idProp,
  imageProp = {},
  nameProp,
  statusProp,
  nutrientsProp,
  prepTimeProp,
  difficultyProp = "easy",
  foodTypeProp = "breakfast",
  dietaryRestrictionsProp = [""],
  ingredientsProp = [],
  stepsProp = [],
  servingsProp,
  descriptionProp,
  macrosBaseProp = "serving",
  hasImage,
  handleRenderStateChange,
  locals,
}) => {
  //callback to close the accordion
  const handleInitialState = useCallback(
    (initial) => {
      if (handleRenderStateChange) {
        handleRenderStateChange(initial);
      }
    },
    [handleRenderStateChange]
  );

  const [tabState, setTabState] = useState("information-tab"); //state to control de active tab

  //state to control the steps of the instruction's form
  const [inputInstructionsFields, setInputInstructionsFields] = useState({
    description: descriptionProp,
    steps: [{ step: "" }],
  });
  //state to control the steps of the ingredient's form
  const [inputIngredientsFields, setInputIngredientsFields] = useState({
    servings: servingsProp,
    steps: [{ ingredient: "", qty: "" }],
  });

  //state to control the rest inputs of the forms (all of them except instructions. dietary state and ingredients fields)
  const [formState, setFormState] = useState({
    name: nameProp || "",
    prepTime: prepTimeProp || "",
    difficulty: difficultyProp || "",
    foodType: foodTypeProp || "",
    macrosBase: macrosBaseProp || "",
    fibers: nutrientsProp ? nutrientsProp.fibers : "",
    fats: nutrientsProp ? nutrientsProp.fats : "",
    carbs: nutrientsProp ? nutrientsProp.carbs : "",
    proteins: nutrientsProp ? nutrientsProp.proteins : "",
  });
  //state to control the dietary state value
  const initialDietaryState =
    dietaryRestrictionsProp === null ? [""] : dietaryRestrictionsProp;
  const [dietaryState, setDietaryState] = useState(initialDietaryState);
  const [imageState, setImageState] = useState(null); //state to know if an image was attached

  const {
    name,
    prepTime,
    difficulty,
    foodType,
    macrosBase,
    fibers,
    fats,
    carbs,
    proteins,
  } = formState;

  const { t } = useTranslation("coaching-portal");

  /**
   * Delete a recipe
   * @param {*} e
   */
  const handleDelete = (e) => {
    if (
      process.browser &&
      confirm(t("widgets.editRecipe.confirmDeleteRecipe"))
    ) {
      e.stopPropagation();
      e.preventDefault();
      handleInitialState({
        isOpen: false,
        recipe: idProp,
      });
      submit(`/actions/recipes/delete/${idProp}`, {
        method: "GET",
      });
    }
  };

  /**
   * Assign value for the formState
   * @param {*} param0
   */
  const handleInputChange = ({ target }) => {
    const newState = {
      ...formState,
      [target.name]:
        target.name === "name" ||
        target.name === "prepTime" ||
        target.name === "foodType" ||
        target.name === "macrosBase" ||
        target.name === "difficulty"
          ? target.value
          : Number.parseInt(target.value, 10),
    };
    setFormState(newState);
  };

  /**
   * Change div's content
   * @param {*} tab
   */
  const handleActiveTab = (tab) => {
    setTabState(tab);
  };

  /**
   * Asign the instructions and ingredients steps to the states
   */
  useEffect(() => {
    const instructionsStepArray = [];
    const insgredientsStepArray = [];
    if (!stepsProp || stepsProp.length === 0) {
      instructionsStepArray.push({ step: "" });
    } else {
      stepsProp.forEach((element, index) => {
        const step = {
          step: element,
          order: index + 1,
        };
        instructionsStepArray.push(step);
      });
    }
    if (!ingredientsProp || ingredientsProp.length === 0) {
      insgredientsStepArray.push({ ingredient: "", qty: "" });
    } else {
      ingredientsProp.forEach((element, index) => {
        const step = {
          ingredient: element.name,
          order: index + 1,
          qty: element.quantity,
        };
        insgredientsStepArray.push(step);
      });
    }

    setInputInstructionsFields({
      ...inputInstructionsFields,
      steps: instructionsStepArray,
    });

    setInputIngredientsFields({
      ...inputIngredientsFields,
      steps: insgredientsStepArray,
    });
  }, []);

  /**
   * Reorder the ingredients
   */
  useEffect(() => {
    let sortable = null;
    if (document.querySelector(".steps-recipe-ingredients")) {
      scriptLoader(
        "https://cdn.jsdelivr.net/npm/sortablejs@1.15.7/Sortable.min.js",
        () => {
          const dragImage = document.createElement("img");
          dragImage.src =
            "data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7";
          sortable = new Sortable(
            document.querySelector(".steps-recipe-ingredients"),
            {
              animation: 350,
              handle: ".drag-handle-ingredient",
              setData(dataTransfer) {
                dataTransfer.setDragImage(dragImage, 0, 0);
              },
              onEnd: (e) => {
                if (e.oldIndex === e.newIndex) return;

                // this will override sortable's default DOM manipulation
                const items = e.from.querySelectorAll(":scope > div");
                e.from.insertBefore(
                  e.item,
                  items[e.oldIndex + (e.oldIndex > e.newIndex)]
                );

                // reorder the ingredients state, this will determine the new DOM order
                // since we stopped sortable's default behavior
                const reorderedIngredients = Array.from(items).map(
                  (element) => {
                    const nameInput = element.querySelector(
                      ".name-input-ingredient-grab"
                    );
                    const quantityInput = element.querySelector(
                      ".quantity-input-ingredient-grab"
                    );
                    return {
                      ingredient: nameInput?.value || "",
                      qty: quantityInput?.value || "",
                    };
                  }
                );

                setInputIngredientsFields({
                  ...inputIngredientsFields,
                  steps: reorderedIngredients,
                });
              },
            }
          );
        }
      );
    }
    return () => {
      if (sortable) sortable.destroy();
    };
  }, [tabState]);

  /**
   * Reorder the instructions steps
   */
  useEffect(() => {
    let sortable = null;
    if (document.querySelector(".steps-recipe-instructions")) {
      scriptLoader(
        "https://cdn.jsdelivr.net/npm/sortablejs@1.15.7/Sortable.min.js",
        () => {
          const dragImage = document.createElement("img");
          dragImage.src =
            "data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7";
          sortable = new Sortable(
            document.querySelector(".steps-recipe-instructions"),
            {
              animation: 350,
              handle: ".drag-handle-steps",
              setData(dataTransfer) {
                dataTransfer.setDragImage(dragImage, 0, 0);
              },
              onEnd: (e) => {
                if (e.oldIndex === e.newIndex) return;

                // this will override sortable's default DOM manipulation
                const items = e.from.querySelectorAll(":scope > div");
                e.from.insertBefore(
                  e.item,
                  items[e.oldIndex + (e.oldIndex > e.newIndex)]
                );

                // reorder the ingredients state, this will determine the new DOM order
                // since we stopped sortable's default behavior
                const reorderedInstructions = Array.from(items).map(
                  (element) => {
                    const nameInput = element.querySelector(
                      ".name-input-instruction-grab"
                    );
                    return {
                      step: nameInput?.value || "",
                    };
                  }
                );

                setInputInstructionsFields({
                  ...inputInstructionsFields,
                  steps: reorderedInstructions,
                });
              },
            }
          );
        }
      );
    }
    return () => {
      if (sortable) sortable.destroy();
    };
  }, [tabState]);

  /**
   * assign the value for the steps of the instruction's state
   * @param {*} index
   * @param {*} event
   */
  const handleFormInstructionsChange = (index, event) => {
    const data = structuredClone(inputInstructionsFields.steps);
    data[index][event.target.name] = event.target.value;
    const newState = { ...inputInstructionsFields, steps: data };
    setInputInstructionsFields(newState);
  };

  /**
   * Create more inputs in the steps of the instruction's steps
   */
  const addInstructionsFields = () => {
    const newInstructionfield = { step: "" };

    const data = structuredClone(inputInstructionsFields.steps);
    data.push(newInstructionfield);
    const newState = { ...inputInstructionsFields, steps: data };
    setInputInstructionsFields(newState);
  };

  /**
   * remove inputs in the steps of the instruction's steps
   * @param {*} index
   */
  const removeInstructionsFields = (index, e) => {
    if (confirm(t("widgets.editRecipe.confirmDeleteStep"))) {
      e.preventDefault();
      const data = structuredClone(inputInstructionsFields.steps);
      data.splice(index, 1);
      const newState = { ...inputInstructionsFields, steps: data };
      setInputInstructionsFields(newState);
    }
  };

  /**
   * assign the value for the steps of the ingredient's state
   * @param {*} index
   * @param {*} event
   */
  const handleFormIngredientsChange = (index, event) => {
    let newState;
    if (index === null) {
      newState = {
        ...inputIngredientsFields,
        servings: Number.parseInt(event.target.value, 10),
      };
    } else {
      const data = structuredClone(inputIngredientsFields.steps);
      data[index][event.target.name] = event.target.value;
      newState = { ...inputIngredientsFields, steps: data };
    }

    setInputIngredientsFields(newState);
  };

  /**
   * Create more inputs in the steps of the ingredient's steps
   */
  const addIngredientsFields = () => {
    const newInstructionfield = { ingredient: "", qty: "" };

    const data = structuredClone(inputIngredientsFields.steps);
    data.push(newInstructionfield);
    const newState = { ...inputIngredientsFields, steps: data };
    setInputIngredientsFields(newState);
  };

  /**
   * remove inputs in the steps of the ingredient's steps
   * @param {*} index
   */
  const removeIngredientsFields = (index, e) => {
    if (confirm(t("widgets.editRecipe.confirmDeleteStep"))) {
      e.preventDefault();
      const data = structuredClone(inputIngredientsFields.steps);
      data.splice(index, 1);
      const newState = { ...inputIngredientsFields, steps: data };
      setInputIngredientsFields(newState);
    }
  };

  /**
   * assign the value for the description of the instruction's state
   * @param {*} param0
   */
  const handleFormInstructionsDescriptionChange = ({ target }) => {
    const newState = { ...inputInstructionsFields, description: target.value };
    setInputInstructionsFields(newState);
  };
  const handleNextTab = (e) => {
    e.preventDefault();
    if (tabState === "information-tab") setTabState("ingredients-tab");
    if (tabState === "ingredients-tab") setTabState("instructions-tab");
    if (tabState === "instructions-tab") setTabState("nutrition-tab");
  };

  /**
   * Validate if edit form is valid
   * @returns
   */

  const validationFormSubmit = () => {
    const isNameValid = formState?.name?.length > 0;
    const isImageValid = imageState !== null || !!imageProp?.s3Path;

    const message = isNameValid
      ? isImageValid
        ? ""
        : t("widgets.editRecipe.imageRequired")
      : t("widgets.editRecipe.nameRequired");

    return { isValid: isNameValid && isImageValid, message };
  };

  const handleImageUpload = (newImage) => {
    setImageState(newImage);
  };
  const difficultyOptions = [
    { text: t("widgets.editRecipe.easy"), value: "easy" },
    { text: t("widgets.editRecipe.medium"), value: "medium" },
    { text: t("widgets.editRecipe.hard"), value: "hard" },
  ];
  return (
    <div className="main-tabs-recipe mobile-hidden">
      <header>
        <nav
          role="tablist"
          id="tabsRecipeSection"
          className={idProp ? "nav-justify-normal" : "nav-justify-around"}
        >
          <button
            id="information-tab"
            onClick={() => handleActiveTab("information-tab")}
            aria-current={tabState === "information-tab" ? true : undefined}
            type="button"
          >
            {t("widgets.editRecipe.information")}
          </button>
          <button
            id="ingredients-tab"
            onClick={() => handleActiveTab("ingredients-tab")}
            aria-current={tabState === "ingredients-tab" ? true : undefined}
            type="button"
          >
            {t("widgets.editRecipe.ingredients")}
          </button>
          <button
            id="instructions-tab"
            aria-current={tabState === "instructions-tab" ? true : undefined}
            onClick={() => handleActiveTab("instructions-tab")}
            type="button"
          >
            {t("widgets.editRecipe.instructions")}
          </button>
          <button
            id="nutrition-tab"
            onClick={() => handleActiveTab("nutrition-tab")}
            aria-current={tabState === "nutrition-tab" ? true : undefined}
            type="button"
          >
            {t("widgets.editRecipe.nutrition")}
          </button>
          {idProp && (
            <button
              onClick={handleDelete}
              data-button-type="secondary"
              data-action="cancel"
              type="button"
            >
              {t("widgets.common.delete")}
            </button>
          )}
        </nav>
      </header>
      <form
        encType="multipart/form-data"
        method="POST"
        data-noreset
        action={
          idProp
            ? `/actions/recipes/save/${idProp}`
            : `/actions/recipes/create/`
        }
        onSubmit={() => handleInitialState({ isOpen: false })}
      >
        {/* information tab */}
        <div
          className={
            tabState === "information-tab" ? "information-tab" : "hidden"
          }
        >
          <div className="flex gap-6">
            <div className="w-[340px]">
              <div>
                <label htmlFor="name">
                  {" "}
                  {t("widgets.editRecipe.recipeName")}
                </label>
                <input
                  name="name"
                  className="text-input"
                  placeholder={t("widgets.editRecipe.namePlaceholder")}
                  value={name}
                  onChange={handleInputChange}
                />
              </div>
              <div>
                <div>
                  <label htmlFor="prepTime">
                    {" "}
                    {t("widgets.editRecipe.prepTime")}{" "}
                  </label>
                  <input
                    name="prepTime"
                    className="text-input"
                    placeholder={t("widgets.editRecipe.prepTime")}
                    value={prepTime}
                    onChange={handleInputChange}
                  />
                </div>
              </div>
            </div>
            <ImageWidgetUploadInput
              name="image"
              prefix={t("widgets.editVideoPlaylist.prefix")}
              defaultValue={imageProp}
              backgroundedit={`${S3.PATH}${imageProp.s3Path}`}
              hasImage={hasImage}
              handleImageUpload={handleImageUpload}
            />
          </div>
          <div className="w-[340px]">
            <label> {t("widgets.editRecipe.difficulty")} </label>
            <Dropdown
              summaryText={
                difficultyOptions.find((option) => option.value === difficulty)
                  ?.text
              }
              options={difficultyOptions}
              onSelectFn={(value) => {
                setFormState({ ...formState, difficulty: value });
              }}
            />
            <input name="difficulty" defaultValue={difficulty} type="hidden" />
          </div>
          <div className="flex gap-6">
            <RadioVerticalGroup
              label={t("widgets.editRecipe.foodType")}
              name="foodType"
              currentValue={foodType}
              radios={FOOD_TYPES.map((ft) => ({
                ...ft,
                label: t(
                  `foodTypes.${ft.value === "lunch/dinner" ? "lunchDinner" : ft.value}`
                ),
              }))}
              onChange={handleInputChange}
            />
            <RadioMultipleVerticalGroup
              label={t("widgets.editRecipe.dietaryRestrictions")}
              name="dietary"
              radios={DIETARY_TYPES.map((dt) => ({
                ...dt,
                label: t(`dietaryTypes.${DIETARY_I18N[dt.value]}`),
              }))}
              currentValue={dietaryState}
              handleStateChange={setDietaryState}
            />
            <input
              name="dietaryRestrictions"
              style={{ display: "none" }}
              readOnly
              value={dietaryState}
            />
            {!idProp && (
              <input
                name="widget"
                style={{ display: "none" }}
                readOnly
                value={locals.query.widget}
              />
            )}
          </div>
        </div>
        {/* ingredients tab */}
        <div
          className={
            tabState === "ingredients-tab" ? "ingredients-tab" : "hidden"
          }
        >
          <div>
            <label htmlFor="servings">
              {" "}
              {t("widgets.editRecipe.numberOfServings")}{" "}
            </label>
            <input
              type="number"
              name="servings"
              value={inputIngredientsFields.servings}
              onChange={(event) => handleFormIngredientsChange(null, event)}
            />
          </div>
          <section>
            <header>
              <span className="steps-title"></span>

              <label>{t("widgets.editRecipe.quantity")}</label>
              <label>{t("widgets.editRecipe.ingredientName")}</label>
            </header>

            <div className="steps-recipe-ingredients">
              {inputIngredientsFields.steps.map((input, index) => {
                return (
                  <div key={index} className="item-card-container" id={index}>
                    <span>{index + 1}.</span>

                    <input
                      name="qty"
                      className="text-input quantity-input-ingredient-grab"
                      placeholder={t("widgets.editRecipe.quantity")}
                      value={input.qty}
                      onChange={(event) =>
                        handleFormIngredientsChange(index, event)
                      }
                    />
                    <input
                      name="ingredient"
                      className="text-input name-input-ingredient-grab"
                      placeholder={t("widgets.editRecipe.namePlaceholder")}
                      value={input.ingredient}
                      onChange={(event) =>
                        handleFormIngredientsChange(index, event)
                      }
                    />
                    <div className="action-handlers">
                      <button
                        onClick={() => removeIngredientsFields(index)}
                        data-button-type="secondary"
                        data-icon
                        className="drag-handle-ingredient"
                        type="button"
                      >
                        <SvgIcon src="/img/new-icons/menu.svg" />
                      </button>
                      <button
                        onClick={(e) => {
                          removeIngredientsFields(index, e);
                        }}
                        data-button-type="secondary"
                        data-icon
                        type="button"
                      >
                        <SvgIcon src="/img/new-icons/red-trash.svg" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
          <footer>
            <button
              type="button"
              data-button-type="outline"
              onClick={addIngredientsFields}
            >
              <span>{t("widgets.editRecipe.addNewIngredient")}</span>
            </button>
          </footer>
        </div>
        {/* instruction tab */}
        <div
          className={
            tabState === "instructions-tab" ? "instructions-tab" : "hidden"
          }
        >
          <header>
            <label htmlFor="description">
              {" "}
              {t("widgets.editVideoPlaylist.description")}{" "}
            </label>
            <textarea
              placeholder={t("widgets.editRecipe.enterDescriptionHere")}
              value={inputInstructionsFields.description}
              onChange={handleFormInstructionsDescriptionChange}
              name="description"
            />
          </header>
          <section>
            <p className="p4 mb-2">{t("widgets.editRecipe.steps")}</p>
            <div className="steps-recipe-instructions">
              {inputInstructionsFields.steps.map((input, index) => {
                return (
                  <div
                    key={index}
                    className="item-card-container step-all-section"
                    id={index}
                  >
                    <span>{index + 1}.</span>

                    <input
                      name="step"
                      className="text-input name-input-instruction-grab"
                      placeholder={t("widgets.editRecipe.namePlaceholder")}
                      value={input.step}
                      onChange={(event) =>
                        handleFormInstructionsChange(index, event)
                      }
                    />

                    <div className="action-handlers">
                      <button
                        data-button-type="secondary"
                        data-icon
                        className="drag-handle-steps"
                        type="button"
                      >
                        <SvgIcon src="/img/new-icons/menu.svg" />
                      </button>
                      <button
                        data-button-type="secondary"
                        data-icon
                        onClick={(e) => removeInstructionsFields(index, e)}
                        type="button"
                      >
                        <SvgIcon src="/img/new-icons/red-trash.svg" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
          <footer>
            <button
              type="button"
              data-button-type="outline"
              onClick={addInstructionsFields}
            >
              <span>{t("widgets.editRecipe.addNewStep")}</span>
            </button>
          </footer>
        </div>
        {/* nutrition tab */}
        <div
          className={tabState === "nutrition-tab" ? "nutrition-tab" : "hidden"}
        >
          <section className="w-[340px]">
            <RadioVerticalGroup
              label={t("widgets.editRecipe.macrosBasedOn")}
              name="macrosBase"
              currentValue={macrosBase}
              radios={MACROS_BASE_TYPES.map((mb) => ({
                ...mb,
                label: t(`macrosBaseTypes.${mb.value}`),
              }))}
              onChange={handleInputChange}
            />
            <label className="label protein req" htmlFor="proteins">
              {t("widgets.editRecipe.proteinLabel")}
            </label>
            <NutrientInput
              widgetStyle={true}
              name="proteins"
              suffix="g"
              value={proteins}
              onChange={handleInputChange}
            />
            <label className="label carbs req" htmlFor="carbs">
              {t("widgets.editRecipe.carbsLabel")}
            </label>
            <NutrientInput
              widgetStyle={true}
              name="carbs"
              suffix="g"
              value={carbs}
              onChange={handleInputChange}
            />
            <label className="label fat req" htmlFor="fats">
              {t("widgets.editRecipe.fatLabel")}
            </label>
            <NutrientInput
              widgetStyle={true}
              name="fats"
              suffix="g"
              value={fats}
              onChange={handleInputChange}
            />
            <label className="label fiber" htmlFor="fibers">
              {t("widgets.editRecipe.fiberOptional")}
            </label>
            <NutrientInput
              widgetStyle={true}
              name="fibers"
              suffix="g"
              value={fibers}
              onChange={handleInputChange}
            />
          </section>

          {tabState === "nutrition-tab" && (
            <NutrientsChartForWidget
              dayStats={{
                nutrients: {
                  proteins: proteins || 0,
                  carbs: carbs || 0,
                  fats: fats || 0,
                  fibers: fibers || 0,
                },
              }}
            />
          )}
        </div>
        <footer>
          {tabState === "nutrition-tab" ? (
            <ToolTip
              id="save-recipe"
              body={validationFormSubmit().message}
              className="recipe-tooltip-container"
            >
              <button
                data-button-type="primary"
                type="submit"
                className="save-macro-btn"
                disabled={!validationFormSubmit().isValid}
              >
                {t("widgets.common.save")}
              </button>
            </ToolTip>
          ) : (
            <button
              data-button-type="primary"
              onClick={handleNextTab}
              type="button"
            >
              {t("widgets.editRecipe.next")}
            </button>
          )}
        </footer>
      </form>
    </div>
  );
};
